// Multi-level undo shared by the form and assessment editors.
// The history array lives in the editor and is passed down to the toolboxes,
// so every helper takes it as an argument rather than owning it.
//
// Entries are serialised rather than objects, so states can be compared
// cheaply and a caller can never hold a live reference into the history.
//
// Only the unit's sections are recorded. The settings tab edits the name,
// description, instructions and the rest on the same unit object and saves them
// separately, so snapshotting the whole unit let "undo" roll those back too.

// How many steps back the editor can go. Units are small enough that keeping a
// short stack of clones is cheap.
const HISTORY_LIMIT = 25;

const UndoHistory = {
    serialise: function (unit) {
        return JSON.stringify((unit && unit.sections) || []);
    },

    // Record the unit as it is *before* a change so the change can be undone.
    // Call this immediately before mutating the unit, not after.
    snapshot: function (history, unit) {
        if (!history || !unit)
            return;

        const state = UndoHistory.serialise(unit);

        // Some actions snapshot without changing anything (dropping a dragged
        // element back where it started, saving a toolbox with no edits).
        // Pushing those would spend an undo step doing nothing visible.
        if (history.length > 0 && history[history.length - 1] === state)
            return;

        history.push(state);

        while (history.length > HISTORY_LIMIT)
            history.shift();
    },

    // Whether there is a state to go back to that actually differs from the one
    // on screen. Sortable fires "sort" once per list involved in a move, so a
    // single drag between two sections snapshots twice and the second entry
    // matches the current state; those entries must not enable the button.
    // serialisedUnit is optional: pass the result of serialise() when the
    // caller already has it, to avoid doing it twice.
    canUndo: function (history, unit, serialisedUnit) {
        if (!history || !unit)
            return false;

        const current = serialisedUnit != null ? serialisedUnit : UndoHistory.serialise(unit);
        for (let i = history.length - 1; i >= 0; i--)
            if (history[i] !== current)
                return true;

        return false;
    },

    // Step back one change, mutating the unit in place.
    // The page component and the mounted toolboxes all hold a reference to this
    // same object, so reassigning it would leave them editing an orphaned copy.
    // Only sections are replaced; everything else on the unit is left alone.
    restore: function (history, unit) {
        if (!history || !unit)
            return false;

        const current = UndoHistory.serialise(unit);
        let state = null;

        // Skip any entries that match what is already on screen, so one press
        // always produces one visible change.
        while (history.length > 0 && state === null) {
            const candidate = history.pop();
            if (candidate !== current)
                state = candidate;
        }

        if (state === null)
            return false;

        unit.sections = JSON.parse(state);

        return true;
    },

    clear: function (history) {
        if (history)
            history.length = 0;
    }
};

export default UndoHistory;
